package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ModifierAst
import top.wcpe.allincore.dsl.compiler.ast.ModifierBundleAst
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.lex.Token
import top.wcpe.allincore.dsl.compiler.lex.TokenType

internal class ModifierBundleParser(private val p: Parser) {

    fun parse(): List<ModifierBundleAst> {
        p.expect(TokenType.IDENTIFIER, "modifierBundles 块缺少名称")
        return parseAfterName()
    }

    fun parseAfterName(): List<ModifierBundleAst> {
        p.expect(TokenType.LBRACE, "modifierBundles 后缺少 '{'")
        p.skipNewlines()
        val bundles = mutableListOf<ModifierBundleAst>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val bundleTok = p.expect(TokenType.IDENTIFIER, "modifierBundles 内必须声明 bundle")
            if (bundleTok.lexeme != "bundle") {
                p.fail("modifierBundles 内仅支持 bundle，实际为 '${bundleTok.lexeme}'", bundleTok.position)
            }
            val nameTok = p.expect(TokenType.IDENTIFIER, "bundle 缺少静态名称")
            p.expect(TokenType.LBRACE, "bundle '${nameTok.lexeme}' 后缺少 '{'")
            p.skipNewlines()
            val modifiers = parseModifierItems()
            val close = p.expect(TokenType.RBRACE, "bundle '${nameTok.lexeme}' 缺少 '}'")
            bundles += ModifierBundleAst(nameTok.lexeme, modifiers, p.completePosition(nameTok.position, close))
            if (!p.check(TokenType.RBRACE)) {
                p.expect(TokenType.NEWLINE, "bundle 声明后缺少换行")
                p.skipNewlines()
            }
        }
        p.expect(TokenType.RBRACE, "modifierBundles 块缺少 '}'")
        return bundles
    }

    fun parseModifierItems(): List<ModifierAst> {
        val modifiers = mutableListOf<ModifierAst>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            modifiers += parseOneModifier()
            if (!p.check(TokenType.RBRACE)) {
                p.expect(TokenType.NEWLINE, "modifier 成员后缺少换行")
                p.skipNewlines()
            }
        }
        return modifiers
    }

    private fun parseOneModifier(): ModifierAst {
        val head = p.current()
        if (head.type == TokenType.KW_IF) {
            return parseConditionalModifier()
        }
        if (head.type != TokenType.IDENTIFIER) {
            p.fail("modifier 内期望静态标识符，实际为 '${head.lexeme}'", head.position)
        }
        if (head.lexeme == "use") {
            return parseUse()
        }
        return parseBlockModifier()
    }

    private fun parseUse(): ModifierAst {
        val useTok = p.advance()
        if (!p.check(TokenType.IDENTIFIER)) {
            p.fail(DslError.AUI_V2_DYNAMIC_USE_FORBIDDEN, useTok.position)
        }
        val nameTok = p.advance()
        if (p.check(TokenType.DOT) || p.check(TokenType.STRING)) {
            p.fail(DslError.AUI_V2_DYNAMIC_USE_FORBIDDEN, nameTok.position)
        }
        return ModifierAst("__use", emptyList(), useTok.position.endingAt(nameTok.range.end), useName = nameTok.lexeme)
    }

    private fun parseBlockModifier(): ModifierAst {
        val kindTok = p.expect(TokenType.IDENTIFIER, "modifier 缺少类型")
        p.expect(TokenType.LBRACE, "modifier '${kindTok.lexeme}' 后缺少 '{'")
        p.skipNewlines()
        val fields = if (kindTok.lexeme == "shaderLayer") {
            p.parseShaderLayerFields(kindTok)
        } else {
            p.parsePlainModifierFields(kindTok)
        }
        val close = p.expect(TokenType.RBRACE, "modifier '${kindTok.lexeme}' 缺少 '}'")
        val kind = if (kindTok.lexeme == "surface") "surface" else kindTok.lexeme
        return ModifierAst(kind, fields, p.completePosition(kindTok.position, close))
    }

    private fun parseConditionalModifier(): ModifierAst {
        val ifTok = p.expect(TokenType.KW_IF)
        p.expect(TokenType.LPAREN, "modifiers.if 后缺少 '('")
        val condition = ExpressionParser(p).parse()
        p.expect(TokenType.RPAREN, "modifiers.if 条件缺少 ')'")
        p.skipNewlines()
        p.expect(TokenType.LBRACE, "modifiers.if 后缺少 '{'")
        p.skipNewlines()
        val children = parseModifierItems().map { it.copy(condition = it.condition ?: condition) }
        val close = p.expect(TokenType.RBRACE, "modifiers.if 块缺少 '}'")
        return ModifierAst(
            "__group",
            emptyList(),
            p.completePosition(ifTok.position, close),
            condition = condition,
            children = children,
        )
    }
}

private fun Parser.parsePlainModifierFields(kindTok: Token): List<ElementAst.PropAssign> {
    val fields = mutableListOf<ElementAst.PropAssign>()
    while (!check(TokenType.RBRACE) && !check(TokenType.EOF)) {
        val fieldTok = parseModifierFieldName("modifier.${kindTok.lexeme} 内缺少字段名")
        expect(TokenType.EQUALS, "modifier.${kindTok.lexeme}.${fieldTok.lexeme} 后缺少 '='")
        fields += ElementAst.PropAssign(
            fieldTok.lexeme,
            ExpressionParser(this, allowBareIdent = true).parse(),
            fieldTok.position,
        )
        consumeModifierFieldSeparator()
    }
    return fields
}

private fun Parser.parseShaderLayerFields(kindTok: Token): List<ElementAst.PropAssign> {
    val fields = mutableListOf<ElementAst.PropAssign>()
    var hasProgram = false
    while (!check(TokenType.RBRACE) && !check(TokenType.EOF)) {
        val fieldTok = parseModifierFieldName("modifier.${kindTok.lexeme} 内缺少字段名")
        rejectInlineShaderSource(fieldTok)
        if (fieldTok.lexeme == "params" && check(TokenType.LBRACE)) {
            fields += parseShaderLayerParams()
            consumeModifierFieldSeparator()
            continue
        }
        expect(TokenType.EQUALS, "modifier.${kindTok.lexeme}.${fieldTok.lexeme} 后缺少 '='")
        if (fieldTok.lexeme == "program") {
            fields += parseStaticProgramField(fieldTok)
            hasProgram = true
        } else {
            fields += ElementAst.PropAssign(
                fieldTok.lexeme,
                ExpressionParser(this, allowBareIdent = true).parse(),
                fieldTok.position,
            )
        }
        consumeModifierFieldSeparator()
    }
    if (!hasProgram) {
        fail(DslError.AUI_V2_SHADER_LAYER_PROGRAM_REQUIRED, kindTok.position)
    }
    return fields
}

private fun Parser.parseStaticProgramField(fieldTok: Token): ElementAst.PropAssign {
    if (!check(TokenType.IDENTIFIER)) {
        fail(DslError.AUI_V2_STATIC_SYMBOL_REQUIRED, current().position, "shaderLayer.program")
    }
    val programTok = advance()
    if (check(TokenType.DOT) || check(TokenType.LPAREN) || check(TokenType.LBRACKET)) {
        fail(DslError.AUI_V2_STATIC_SYMBOL_REQUIRED, programTok.position, "shaderLayer.program")
    }
    return ElementAst.PropAssign(
        fieldTok.lexeme,
        ExprAst.Literal.StringLit(programTok.lexeme, programTok.position),
        fieldTok.position,
    )
}

private fun Parser.parseShaderLayerParams(): List<ElementAst.PropAssign> {
    expect(TokenType.LBRACE, "shaderLayer.params 后缺少 '{'")
    skipNewlines()
    val fields = mutableListOf<ElementAst.PropAssign>()
    while (!check(TokenType.RBRACE) && !check(TokenType.EOF)) {
        val paramTok = parseModifierFieldName("shaderLayer.params 内缺少参数名")
        rejectInlineShaderSource(paramTok)
        expect(TokenType.EQUALS, "shaderLayer.params.${paramTok.lexeme} 后缺少 '='")
        fields += ElementAst.PropAssign(
            "params.${paramTok.lexeme}",
            ExpressionParser(this, allowBareIdent = true).parse(),
            paramTok.position,
        )
        if (!check(TokenType.RBRACE)) {
            expect(TokenType.NEWLINE, "shaderLayer.params 字段后缺少换行")
            skipNewlines()
        }
    }
    expect(TokenType.RBRACE, "shaderLayer.params 缺少 '}'")
    return fields
}

private fun Parser.consumeModifierFieldSeparator() {
    if (!check(TokenType.RBRACE)) {
        expect(TokenType.NEWLINE, "modifier 字段后缺少换行")
        skipNewlines()
    }
}

private fun Parser.rejectInlineShaderSource(fieldTok: Token) {
    if (fieldTok.lexeme in INLINE_SHADER_SOURCE_FIELDS) {
        fail(DslError.AUI_V2_SHADER_INLINE_SOURCE_FORBIDDEN, fieldTok.position)
    }
}

private fun Parser.parseModifierFieldName(message: String): Token {
    val current = current()
    if (current.type == TokenType.IDENTIFIER || current.type == TokenType.KW_COLOR) {
        return advance()
    }
    fail("$message：期望 IDENTIFIER，实际为 ${current.type}（'${current.lexeme}'）", current.position)
}

private val INLINE_SHADER_SOURCE_FIELDS = setOf(
    "source",
    "shader",
    "glsl",
    "fragment",
    "vertex",
    "fragmentSource",
    "vertexSource",
    "shaderSource",
)
